<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import type { FetchTicketStatusResponse, NestedTicketStatus, NestedTicketStatusDetail } from 'src/api/models';
import { useDropdownState } from 'src/composables/useDropdownState';
import { useTicketService } from 'src/services/ticket-service';
import SharedDropdown from 'src/components/shared/SharedDropdown.vue';
import { getStatusColors } from './ticket-display-data';
import 'src/assets/ticket-edit-property.css';

const props = defineProps<{
  status?: NestedTicketStatus | null;
  statusDetail?: NestedTicketStatusDetail | null;
  showStatus?: boolean;
}>();

const emit = defineEmits<{
  'update:status': [value: NestedTicketStatus | null];
  'update:statusDetail': [value: NestedTicketStatusDetail | null];
}>();

const statusEl = ref<HTMLElement>();
const statusDropdown = useDropdownState(statusEl);

const detailEl = ref<HTMLElement>();
const detailDropdown = useDropdownState(detailEl);

const { fetchAllTicketStatuses } = useTicketService();
const statuses = ref<FetchTicketStatusResponse[]>([]);
const selectedStatus = ref<FetchTicketStatusResponse | null>(null);
const selectedStatusDetail = ref<NestedTicketStatusDetail | null>(null);

const hasStatusDetails = computed(() => (selectedStatus.value?.status_details?.length ?? 0) > 0);
const statusLabel = computed(() => selectedStatus.value?.label || selectedStatus.value?.name || 'Select');
const statusColors = computed(() =>
  getStatusColors(selectedStatus.value?.name ?? props.status?.name),
);

async function loadStatuses(): Promise<void> {
  if (!props.showStatus) return;
  try {
    statuses.value = (await fetchAllTicketStatuses()) as FetchTicketStatusResponse[];
    syncStatusFromProps();
  } catch {
    console.error('Failed to load statuses');
  }
}

function syncStatusFromProps(): void {
  if (!props.status?.id) return;
  const found = statuses.value.find((s) => s.id === props.status?.id);
  if (!found) return;
  selectedStatus.value = found;
  if (props.statusDetail?.id && found.status_details?.length) {
    selectedStatusDetail.value = found.status_details.find((d) => d.id === props.statusDetail?.id) ?? null;
  }
}

function selectStatus(s: FetchTicketStatusResponse): void {
  selectedStatus.value = s;
  selectedStatusDetail.value = null;
  emit('update:status', { id: s.id, name: s.name, label: s.label, visible_on_board: s.visible_on_board });
  statusDropdown.close();
  if ((s.status_details?.length ?? 0) > 0) {
    detailDropdown.open();
  }
}

function selectStatusDetail(d: NestedTicketStatusDetail): void {
  selectedStatusDetail.value = d;
  emit('update:statusDetail', { id: d.id, name: d.name });
  detailDropdown.close();
}

function toggleDetailDropdown(): void {
  detailDropdown.toggle();
  statusDropdown.close();
}

function toggleStatusDropdown(): void {
  statusDropdown.toggle();
  detailDropdown.close();
}

watch(() => props.status, () => { if (props.showStatus) syncStatusFromProps(); }, { deep: true });

onMounted(() => {
  if (props.showStatus) loadStatuses();
});
</script>

<template>
  <div v-if="showStatus">
    <div ref="statusEl" class="relative">
      <div class="prop-row" @click="toggleStatusDropdown">
        <span class="prop-label">Status</span>
        <span :class="['inline-flex items-center gap-1.5 text-s font-semibold', statusColors.text]">
          <span :class="['w-1.5 h-1.5 rounded-full', statusColors.dot]" />
          {{ statusLabel }}
          <i class="pi pi-chevron-down prop-chevron" />
        </span>
      </div>
      <SharedDropdown
        :open="statusDropdown.isOpen.value"
        :items="statuses" :active-id="selectedStatus?.id"
        max-height="300px" @select="selectStatus"
      >
        <template #item="{ item }">{{ item.label || item.name }}</template>
      </SharedDropdown>
    </div>

    <div v-if="hasStatusDetails" ref="detailEl" class="relative">
      <div class="prop-row" @click="toggleDetailDropdown">
        <span class="prop-label">Detail</span>
        <span class="text-s font-medium text-surface-700 dark:text-surface-300">
          {{ selectedStatusDetail?.name || 'Select' }}
          <i class="pi pi-chevron-down prop-chevron" />
        </span>
      </div>
      <SharedDropdown
        :open="detailDropdown.isOpen.value"
        :items="selectedStatus?.status_details ?? []" :active-id="selectedStatusDetail?.id"
        max-height="300px" @select="selectStatusDetail"
      />
    </div>
  </div>
</template>
